import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Receipt, Calendar, Users, Download, Eye, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort by date, most recent first
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setBookings(sorted);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (booking) => {
    if (booking.booking_status === 'confirmed') {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Confirmed</span>;
    }
    if (booking.booking_status === 'cancelled') {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">Cancelled</span>;
    }
    if (booking.payment_status === 'paid') {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Deposit Paid</span>;
    }
    return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">Pending Payment</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const downloadReceipt = (booking) => {
    // Generate a simple receipt as text/HTML for now
    const receiptContent = `
CHIMBA TRAVEL - BOOKING RECEIPT
================================

Order Number: ${booking.order_number || booking.id.slice(0, 8).toUpperCase()}
Date: ${formatDate(booking.created_at)}

PACKAGE DETAILS
---------------
Package: ${booking.package_title}
Travel Dates: ${booking.selected_trip_date || booking.preferred_dates}
Group Size: ${booking.group_size} traveler(s)

PAYMENT DETAILS
---------------
Package Price: $${booking.package_price.toFixed(2)} per person
Deposit Paid (5%): $${booking.deposit_amount.toFixed(2)}
Payment Status: ${booking.payment_status === 'paid' ? 'PAID' : 'PENDING'}

CUSTOMER INFORMATION
--------------------
Name: ${booking.customer_name}
Email: ${booking.customer_email}
Phone: ${booking.customer_phone}

${booking.message ? `Special Requests:\n${booking.message}` : ''}

================================
Thank you for choosing Chimba Travel!
Questions? Contact us at hello@chimbatravel.com
or call (833) 568-8090
    `;

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ChimbaTravel_Receipt_${booking.order_number || booking.id.slice(0, 8)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-bookings">
      <div className="card">
        <h2 className="text-2xl font-heading font-bold text-maroon mb-2">My Bookings</h2>
        <p className="text-maroon/70 mb-6">View your booking history and download receipts</p>

        {bookings.length === 0 ? (
          <div className="text-center py-12 bg-sand/20 rounded-sm">
            <Receipt size={48} className="mx-auto text-maroon/30 mb-4" />
            <p className="text-maroon/70 mb-4">No bookings yet</p>
            <Link to="/partners" className="btn-primary inline-block">
              Browse Packages
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div 
                key={booking.id}
                className="border-2 border-maroon/10 rounded-sm overflow-hidden hover:border-maroon/30 transition-colors"
              >
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading font-bold text-lg text-maroon">
                          {booking.package_title}
                        </h3>
                        {getStatusBadge(booking)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-maroon/70">
                        <span className="flex items-center gap-1">
                          <Receipt size={14} />
                          {booking.order_number || booking.id.slice(0, 8).toUpperCase()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {booking.selected_trip_date || booking.preferred_dates}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={14} />
                          {booking.group_size} traveler{booking.group_size !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-maroon/60">Deposit Paid</p>
                      <p className="text-xl font-bold text-terracotta">
                        ${booking.deposit_amount.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-maroon/10">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedBooking(selectedBooking?.id === booking.id ? null : booking)}
                      className="border-maroon/30 text-maroon hover:bg-maroon/5"
                    >
                      <Eye size={16} className="mr-1" />
                      {selectedBooking?.id === booking.id ? 'Hide Details' : 'View Details'}
                    </Button>
                    {booking.payment_status === 'paid' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadReceipt(booking)}
                        className="border-maroon/30 text-maroon hover:bg-maroon/5"
                      >
                        <Download size={16} className="mr-1" />
                        Download Receipt
                      </Button>
                    )}
                    <Link 
                      to={`/packages/${booking.package_id}`}
                      className="inline-flex items-center px-3 py-1.5 text-sm border border-maroon/30 rounded-sm text-maroon hover:bg-maroon/5"
                    >
                      <MapPin size={16} className="mr-1" />
                      View Package
                    </Link>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedBooking?.id === booking.id && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 bg-sand/20 border-t border-maroon/10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                      <div>
                        <h4 className="font-semibold text-maroon mb-2">Booking Details</h4>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Order Number:</dt>
                            <dd className="text-maroon font-medium">{booking.order_number || booking.id.slice(0, 8).toUpperCase()}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Booked On:</dt>
                            <dd className="text-maroon">{formatDate(booking.created_at)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Package Price:</dt>
                            <dd className="text-maroon">${booking.package_price.toFixed(2)}/person</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Deposit (5%):</dt>
                            <dd className="text-terracotta font-semibold">${booking.deposit_amount.toFixed(2)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Payment Status:</dt>
                            <dd className={`font-medium ${booking.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                              {booking.payment_status === 'paid' ? 'Paid' : 'Pending'}
                            </dd>
                          </div>
                        </dl>
                      </div>
                      <div>
                        <h4 className="font-semibold text-maroon mb-2">Contact Information</h4>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Name:</dt>
                            <dd className="text-maroon">{booking.customer_name}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Email:</dt>
                            <dd className="text-maroon">{booking.customer_email}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-maroon/60">Phone:</dt>
                            <dd className="text-maroon">{booking.customer_phone}</dd>
                          </div>
                        </dl>
                        {booking.message && (
                          <div className="mt-3">
                            <h4 className="font-semibold text-maroon mb-1">Special Requests</h4>
                            <p className="text-sm text-maroon/70">{booking.message}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="card bg-sand/30">
        <h3 className="font-heading font-bold text-maroon mb-2">Need Help?</h3>
        <p className="text-sm text-maroon/70 mb-4">
          If you have questions about your booking or need to make changes, our team is here to help.
        </p>
        <div className="flex flex-wrap gap-4">
          <a href="mailto:hello@chimbatravel.com" className="text-maroon hover:text-terracotta underline text-sm">
            hello@chimbatravel.com
          </a>
          <a href="tel:+18335688090" className="text-maroon hover:text-terracotta underline text-sm">
            (833) 568-8090
          </a>
        </div>
      </div>
    </div>
  );
};

export default MyBookings;
